<template>
    <form-wrapper>
        <form-row class="reservation-filter__row">
            <form-cell width="third">
                <select-input
                    :formData="data"
                    :label="$t('forms.chart.chart_filter.statistics')"
                    :options="statisticOptions"
                    :isRequired="true"
                    valuePath="statisticOption"
                    @change="changedStatisticOption"
                ></select-input>
            </form-cell>
            <form-cell width="third">
                <select-input
                    :formData="data"
                    :label="$t('forms.reservations.reservation_filter.fields.year.label')"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.year.placeholder')"
                    :options="yearOptions"
                    valuePath="year"
                    @change="changeWithSubmit"
                ></select-input>
            </form-cell>
            <form-cell v-if="!showUserCompanyOptions && reminderOptions" width="third">
                <select-input
                    :formData="data"
                    :label="$t('forms.chart.chart_filter.reminder.label')"
                    :placeholder="$t('forms.chart.chart_filter.reminder.placeholder')"
                    :options="reminderOptions"
                    :isNullable="true"
                    valuePath="customReminderId"
                    @change="changeWithSubmit"
                    @clear="clearCustomReminderId"
                ></select-input>
            </form-cell>
            <form-cell v-if="showUserCompanyOptions" width="third">
                <multi-select-input
                    v-if="isStatisticForUser"
                    class="my-calendar__filter--select"
                    :label="$t('pages.calendar.my_calendar.offer_user')"
                    :placeholder="$t('common.microcopy.select')"
                    :options="userOfferOptions"
                    :value="selectedUserOfferOptions"
                    :showSelectAllOption="true"
                    @change="changeUserOptions"
                ></multi-select-input>
                <multi-select-input
                    v-else
                    class="my-calendar__filter--select"
                    :label="$t('pages.calendar.my_calendar.offer_company')"
                    :placeholder="$t('common.microcopy.select')"
                    :options="companyOfferOptions"
                    :value="selectedCompanyOfferOptions"
                    :showSelectAllOption="true"
                    :hasExpandableOptions="true"
                    @change="changeCompanyOptions"
                ></multi-select-input>
            </form-cell>
            <form-cell v-if="showStatusOption" width="third">
                <multi-select-input
                    :formData="data"
                    valuePath="status"
                    :label="$t('forms.reservations.reservation_filter.fields.status.label')"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.status.placeholder')"
                    :options="statusOptions"
                    :showSelectAllOption="true"
                    @change="changeWithSubmit"
                ></multi-select-input>
            </form-cell>
            <form-cell v-if="showCurrency" width="third">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.currency.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.currency.placeholder')"
                    :options="currencyOptions"
                    :isRequired="true"
                    valuePath="currency"
                    @change="changeWithSubmit"
                ></select-input>
            </form-cell>
            <form-cell v-if="showSearchByClient" width="third">
                <common-client-search-autocomplete
                    ref="clientAC"
                    :companyId="+$route.params.id"
                    :upperLabel="$t('pages.company.my_company.tabs.client')"
                    :addNewClientAvailable="false"
                    :chosenClient="chosenClient"
                    avatarSize="small"
                    inputStyle="input"
                    @client-selected="onClientSelected"
                    @client-removed="onClientRemoved"
                    @toggle-client-preview="isClientSearchPreview = !isClientSearchPreview"
                ></common-client-search-autocomplete>
            </form-cell>
            <form-cell v-if="showSearchByClient && locations.length > 1" width="third">
                <select-input
                    v-if="showSearchByClient && locations.length > 1"
                    :formData="data"
                    :isNullable="true"
                    valuePath="locationId"
                    :label="$t('pages.company.my_company.tabs.locations')"
                    :placeholder="$t('forms.reservations.reservation_filter.fields.location.placeholder')"
                    :options="locations"
                    @change="locationChange"
                    @clear="clearLocation(event)"
                ></select-input>
            </form-cell>
            <form-cell v-if="showSearchByClient && companyUsers.length > 1" width="third">
                <common-user-search
                    :upperLabel="$t('pages.company.my_company.tabs.performers')"
                    :placeholder="$t('pages.company.my_company.search_performer')"
                    :items="companyUsers"
                    avatarSize="small"
                    inputStyle="input"
                    :selectedUser="selectedPerformer"
                    @user-selected="onPerformerSelected"
                    @user-removed="onPerformerRemoved"
                ></common-user-search>
            </form-cell>
        </form-row>
    </form-wrapper>
</template>

<script>
    import ReservationStatusFilterEnum from 'enums/reservation_status_filter.enum'
    import OfferAPI from 'api/OfferAPI'
    import CompanyAPI from 'api/CompanyAPI'
    import FormMixin from 'mixins/FormMixin'
    import cloneDeep from 'lodash.clonedeep'
    import CurrencyEnum from 'enums/currency.enum'
    import CurrencyOptionsEnum from 'enums/currency_options.enum'
    import StatisticOptionsEnum from 'enums/statistic_options.enum'
    import StatisticOptionsUserEnum from 'enums/statistic_options_user.enum'
    import LanguageEnum from 'enums/language.enum'
    import dayjs from 'dayjs'
    import CompanyRoles from 'enums/company_roles.enum'
    import AvailabilityAPI from 'api/AvailabilityAPI'

    export default {
        mixins: [FormMixin],

        props: {
            isStatisticForUser: { type: Boolean, default: false },
        },

        data() {
            return {
                userOfferOptions: [],
                selectedUserOfferOptions: [],
                companyOfferOptions: [],
                selectedCompanyOfferOptions: [],
                excludeOfferIds: [],
                currencyOptions: Object.values(CurrencyEnum).map((currency) => ({ label: currency, value: currency })),
                currency: undefined,
                showCurrency: false,
                showUserCompanyOptions: true,
                showStatusOption: true,
                allUserOfferIds: [],
                allCompanyOfferIds: [],
                reminderOptions: [],
                currentYear: new Date().getFullYear(),
                clientsList: [],
                chosenClient: {},
                chosenClientId: undefined,
                companyUsers: [],
                chosenPerformerId: undefined,
                locations: [],
                selectedPerformer: undefined,
            }
        },

        computed: {
            statusOptions() {
                return Object.values(ReservationStatusFilterEnum).map((status) => ({
                    label: this.$t(`components.reservations.reservation_status.statuses.${status.label}`),
                    status,
                    value: status.value,
                }))
            },

            statisticOptions() {
                const statOptions = this.isStatisticForUser
                    ? Object.values(StatisticOptionsUserEnum)
                    : Object.values(StatisticOptionsEnum)

                return statOptions.map((option) => ({
                    label: this.$t(`forms.chart.chart_filter.statistic_option.${option}`),
                    value: option,
                }))
            },

            yearOptions() {
                const startYear = 2024
                const years = []

                for (let year = startYear; year <= this.currentYear; year++) {
                    years.push({ label: year.toString(), value: year })
                }

                return years
            },

            showSearchByClient() {
                const userCompanyRole = this.$auth
                    .getUser()
                    .companyUsers?.find((item) => item.company?.id === Number(this.$route.params?.id))?.companyRole

                return (
                    [StatisticOptionsEnum.REVENUE, StatisticOptionsEnum.OFFER].includes(this.data.statisticOption) &&
                    userCompanyRole === CompanyRoles.COMPANY_ADMIN.VALUE
                )
            },
        },

        methods: {
            loadMyOffers() {
                OfferAPI.getMy().then((offers) => {
                    if (this.isStatisticForUser) {
                        this.userOfferOptions = offers.personalOffers.map((offer) => ({
                            label: offer.titleTextContent.originalText,
                            value: offer.id,
                        }))
                        this.selectedUserOfferOptions = this.userOfferOptions.map((option) => option.value)
                        this.allUserOfferIds = cloneDeep(this.selectedUserOfferOptions)
                    } else {
                        this.companyOfferOptions = offers.companies
                            .filter((company) => company.id === Number(this.$route.params.id))
                            .map((company) => ({
                                heading: company.name,
                                value: company.id,
                                options: company.offers.map((offer) => ({
                                    label: offer.titleTextContent.originalText,
                                    value: offer.id,
                                })),
                            }))
                        this.selectedCompanyOfferOptions = this.companyOfferOptions.reduce((acc, companies) => {
                            return [...acc, ...companies.options.map((option) => option.value)]
                        }, [])
                        this.allCompanyOfferIds = cloneDeep(this.selectedCompanyOfferOptions)
                    }
                })
            },
            loadCustomReminders() {
                CompanyAPI.getCustomReminders(this.$route.params.id).then((reminders) => {
                    this.reminderOptions = reminders?.map((reminder) => ({
                        value: reminder.id,
                        label: reminder.reminderName,
                    }))
                })
            },
            changeWithSubmit({ path, value }) {
                this.change({ path, value })
                this.submit()
            },

            clearCustomReminderId(event) {
                this.clear(event)
                this.data.clearCustomReminderId = undefined
                this.submit()
            },

            changedStatisticOption({ path, value }) {
                if (
                    value === StatisticOptionsEnum.CUSTOM_RESERVATION_SEND ||
                    value === StatisticOptionsEnum.CUSTOM_RESERVATION_PRICE
                ) {
                    this.chosenClientId = undefined
                    this.chosenClient = {}
                    this.showCurrency = false
                    this.showUserCompanyOptions = false
                    this.showStatusOption = false
                } else if (value === StatisticOptionsEnum.REVENUE) {
                    this.showCurrency = true
                    this.showUserCompanyOptions = true
                    this.showStatusOption = true
                } else {
                    this.showCurrency = false
                    this.showUserCompanyOptions = true
                    this.showStatusOption = true
                }
                this.change({ path, value })
                this.submit()
            },

            changeUserOptions({ path, value }) {
                this.selectedUserOfferOptions = value
                this.change({ path, value })
                this.changeExcludedUserOfferIds()
            },

            changeCompanyOptions({ path, value }) {
                this.selectedCompanyOfferOptions = value
                this.change({ path, value })
                this.changeExcludedCompanyOfferIds()
            },

            changeExcludedUserOfferIds() {
                this.excludeOfferIds = this.allUserOfferIds.filter((el) => !this.selectedUserOfferOptions.includes(el))
                this.data.excludeOfferIds = this.excludeOfferIds
                this.submit()
            },

            changeExcludedCompanyOfferIds() {
                this.excludeOfferIds = this.allCompanyOfferIds.filter((el) => !this.selectedCompanyOfferOptions.includes(el))
                this.data.excludeOfferIds = this.excludeOfferIds
                this.submit()
            },
            async submit() {
                const query = {}
                query.statisticOption = this.data.statisticOption

                if (this.data.customReminderId) {
                    query.customReminderId = this.data.customReminderId
                }

                if (this.data.year) {
                    const year = this.data.year
                    query.dateFrom = dayjs().year(year).startOf('year').format('YYYY-MM-DD')
                    query.dateTo = dayjs().year(year).endOf('year').format('YYYY-MM-DD')
                }

                if (this.data.status) {
                    query.status = this.data.status
                }

                if (this.data.excludeOfferIds && this.data.excludeOfferIds.length) {
                    query.excludeOfferIds = this.data.excludeOfferIds
                }

                if (this.chosenClientId) {
                    query.initiatorId = this.chosenClientId
                }

                if (this.chosenPerformerId) {
                    query.performerId = this.chosenPerformerId
                }

                if (this.data.locationId) {
                    query.locationId = this.data.locationId
                }

                if (this.showCurrency) {
                    query.currency = this.data.currency
                } else if (query.statisticOption) {
                    query.currency = this.data.currency
                } else {
                    delete query.currency
                }

                this.emitEvent({ name: 'filter-change', payload: query })
            },

            onClientSelected(clientId) {
                this.chosenClientId = clientId
                this.submit()
            },

            onPerformerSelected(performer) {
                this.selectedPerformer = performer
                this.chosenPerformerId = performer.id
                this.submit()
            },

            onClientRemoved() {
                this.chosenClientId = undefined
                this.submit()
            },

            onPerformerRemoved() {
                this.selectedPerformer = undefined
                this.chosenPerformerId = undefined
                this.submit()
            },

            getCompany() {
                const companyId = this.$route.params.id
                CompanyAPI.getById(companyId).then((company) => {
                    company.locationsForCompany.map((location) => {
                        this.locations.push({
                            label: location.address,
                            value: location.id,
                        })
                    })
                    company.companyUsers.map((user) => {
                        this.companyUsers.push(user.user)
                    })
                })
            },

            locationChange({ path, value }) {
                this.selectedPerformer = undefined
                this.chosenPerformerId = undefined
                AvailabilityAPI.getAvailabilityPerformers(`locationId=${value}`).then((res) => {
                    this.companyUsers = res
                })

                this.change({ path, value })
                this.submit()
            },

            clearLocation() {
                this.chosenPerformerId = undefined
                AvailabilityAPI.getAvailabilityPerformers().then((res) => {
                    this.companyUsers = res
                })
                delete this.data.locationId
                this.submit()
            },
        },

        mounted() {
            this.loadMyOffers()
            if (!this.isStatisticForUser) {
                this.loadCustomReminders()
                this.getCompany()
            }
            const data = []
            data.status = this.statusOptions
                .filter((item) =>
                    [
                        ReservationStatusFilterEnum.DONE.value,
                        ReservationStatusFilterEnum.ACCEPTED.value,
                        ReservationStatusFilterEnum.OPEN.value,
                    ].includes(item.value),
                )
                .map((item) => item.value)
            if (this.$i18n.locale === LanguageEnum.SR) {
                data.currency = this.currencyOptions[CurrencyOptionsEnum.RSD].value
            } else if (this.$i18n.locale === LanguageEnum.BIH) {
                data.currency = this.currencyOptions[CurrencyOptionsEnum.BAM].value
            } else {
                data.currency = this.currencyOptions[CurrencyOptionsEnum.EUR].value
            }
            data.statisticOption = StatisticOptionsEnum.OFFER
            data.year = this.currentYear
            this.prefillData(data)

            if (this.$route.query.client) {
                this.chosenClient = JSON.parse(this.$route.query.client)
                this.chosenClientId = this.chosenClient.id
            }
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/mixins';
    @import 'styles/components/offers/_offer-filter.scss';

    .reservation-filter__row {
        display: flex;
        flex-wrap: wrap;
    }
</style>
